import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRebaseClient } from "@rebasepro/app";
import { useTeam } from "./_team";
import { plural } from "../plural";
import jobsCollection from "../collections/jobs";
import type { RebaseClient } from "@rebasepro/types";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Card as RebaseCard,
    CircularProgressCenter as RebaseCircularProgressCenter,
    IconButton as RebaseIconButton,
    Tooltip as RebaseTooltip,
    Typography as RebaseTypography,
    RefreshCwIcon as RebaseRefreshCwIcon,
    cls,
    cardClickableMixin,
    defaultBorderMixin,
    surfaceHoverMixin,
    iconSize
} from "@rebasepro/ui";
// The kit re-exports only the icons its own packages use, so the tile icons come
// straight from lucide — same convention as MatchesPanel.tsx.
import {
    Briefcase as RebaseBriefcaseIcon,
    Building2 as RebaseBuildingIcon,
    Coins as RebaseCoinsIcon,
    DownloadCloud as RebaseCloudDownloadIcon,
    // `Handshake`, not `ContactRound`: this package resolves to 0.344 locally
    // and to the 1.27 that @rebasepro/ui pins inside the Docker build, and
    // 0.344 has no ContactRound. Same class of trap as the brand icons in
    // ProspectContactsPanel, caught here by tsc rather than twelve minutes
    // into a production rollup build.
    Handshake as RebaseHandshakeIcon,
    Inbox as RebaseInboxIcon,
    Mail as RebaseMailIcon,
    UserCog as RebaseUserCogIcon,
    Users as RebaseUsersIcon
} from "lucide-react";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const CircularProgressCenter = RebaseCircularProgressCenter as React.ComponentType<any>;
const IconButton = RebaseIconButton as React.ComponentType<any>;
const Tooltip = RebaseTooltip as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const RefreshCwIcon = RebaseRefreshCwIcon as React.ComponentType<any>;
const BriefcaseIcon = RebaseBriefcaseIcon as React.ComponentType<any>;
const BuildingIcon = RebaseBuildingIcon as React.ComponentType<any>;
const CoinsIcon = RebaseCoinsIcon as React.ComponentType<any>;
const CloudDownloadIcon = RebaseCloudDownloadIcon as React.ComponentType<any>;
const HandshakeIcon = RebaseHandshakeIcon as React.ComponentType<any>;
const InboxIcon = RebaseInboxIcon as React.ComponentType<any>;
const MailIcon = RebaseMailIcon as React.ComponentType<any>;
const UserCogIcon = RebaseUserCogIcon as React.ComponentType<any>;
const UsersIcon = RebaseUsersIcon as React.ComponentType<any>;

/**
 * How long an item can sit before it stops being "the queue" and starts being
 * "late". Only the two queues where a *person* is waiting on a reply have a
 * deadline; nobody is owed an answer by a scraped job posting.
 */
const OVERDUE_DAYS = { applications: 7, inbound: 7 };

/**
 * Tile accents, keyed by tone.
 *
 * Colour here means one thing only: something is overdue. Work simply *being*
 * in a queue is the normal state of a recruiting panel — painting four tiles
 * red and orange on a Tuesday morning reads as four alarms and trains everyone
 * to ignore the colour by Wednesday. So a populated queue is calm and legible,
 * and amber is spent only when an item has actually aged past its threshold.
 */
const TONE_STYLES: Record<string, { value: string; icon: string; ring: string }> = {
    // Something has waited too long. The only alarm on the page.
    overdue: {
        value: "text-amber-600 dark:text-amber-400",
        icon: "text-amber-600 dark:text-amber-400 bg-amber-500/10",
        ring: "border-amber-500/30"
    },
    // Work waiting, on time. Present and readable, not shouting.
    pending: {
        value: "text-text-primary dark:text-text-primary-dark",
        icon: "text-blue-600 dark:text-blue-400 bg-blue-500/10",
        ring: ""
    },
    // Empty queue: the good state, and it should recede.
    calm: {
        value: "opacity-40",
        icon: "text-text-secondary dark:text-text-secondary-dark bg-surface-raised",
        ring: ""
    }
};

/**
 * Route builders.
 *
 * Collections are mounted under `/c/<slug>` and custom views at `/<slug>` —
 * getting that wrong is a dead link rather than an error, so both live here
 * instead of being spelled out at each call site. Hardcoding the `/c` prefix is
 * safe while the admin runs at the default `basePath`; `useUrlController`,
 * which would derive it, is not exported from `@rebasepro/cms`.
 */
/**
 * A count for the screen. `null` — the request failed — reads as "—", never as
 * a number, and never as the word "null" inside a sentence.
 */
const num = (value: number | null | undefined): string =>
    value === null || value === undefined ? "—" : String(value);

/** Counts added together, or null if any of them failed — see `Counts`. */
const sum = (...values: (number | null)[]): number | null =>
    values.includes(null) ? null : (values as number[]).reduce((a, b) => a + b, 0);

const collectionPath = (slug: string) => `/c/${slug}`;
const viewPath = (slug: string) => `/${slug}`;

type UrlFilter = Record<string, [string, unknown]>;

/**
 * A collection that opens already filtered.
 *
 * `<field>_op=<op>&<field>_value=<value>` is what the table reads back out of
 * its URL (`parseFilterAndSort` in rebase's useDataTableController), with the
 * value JSON-parsed — so `null` arrives as null and `[]` as the empty list. One
 * condition per field is all it reads.
 *
 * A filter in the URL replaces the collection's `defaultFilter` rather than
 * joining it. For the jobs list that is what these links need: it opens on
 * `status != closed`, and a count taken over every vacancy would otherwise land
 * on a list missing the closed ones.
 */
const filteredCollectionPath = (slug: string, where: UrlFilter) =>
    `${collectionPath(slug)}?${Object.entries(where)
        .map(([field, [op, value]]) =>
            `${encodeURIComponent(`${field}_op`)}=${encodeURIComponent(op)}` +
            `&${encodeURIComponent(`${field}_value`)}=${encodeURIComponent(JSON.stringify(value))}`)
        .join("&")}`;

/**
 * The questions behind "Datos por completar". Each is both the count on this
 * page and the filter on the list its link opens, so the number and the rows
 * are one query asked twice rather than two that happen to agree.
 *
 * "No areas" is stored two ways: NULL where nothing ever wrote the column, and
 * `{}` where something wrote an empty list — the site's submission form, the
 * company dashboard and the scraper's cleaner all do. No filter operator is
 * true of both, and a link carries one condition per field, so they are asked
 * separately; `areasHref` below decides what the number can link to.
 */
const JOBS_WITHOUT_COMPANY: UrlFilter = { company_id: ["is-null", null] };
const JOBS_AREAS_UNSET: UrlFilter = { sustainability_focus: ["is-null", null] };
const JOBS_AREAS_EMPTY: UrlFilter = { sustainability_focus: ["==", []] };

/**
 * What `/c/jobs` opens on, read from the collection rather than restated.
 *
 * The legend's "Vacantes" links to the plain collection, the drawer's own
 * target, so its count can only be this filter. It was every vacancy: 76 on
 * sustentalent_local beside a link that lands on 49, because the list opens on
 * `status != closed`. Taken from `jobsCollection`, a change to that default
 * moves the number with it instead of leaving it to disagree.
 */
const JOBS_DEFAULT_LIST = jobsCollection.admin?.defaultFilter;

/**
 * On the public board. `published` is the one status the site can read at
 * all (`public_read_published` in collections/jobs.ts), so this is what
 * sustentalent.com shows — narrower than the list above whenever something is
 * a draft or waiting for review.
 */
const JOBS_ON_BOARD: UrlFilter = { status: ["==", "published"] };
const JOBS_ON_BOARD_INTERNAL: UrlFilter = { ...JOBS_ON_BOARD, application_method: ["==", "internal"] };

/**
 * "Nobody has taken this yet", asked of both inbound tables. `employer_key IS
 * NULL` is the column's own definition (drizzle/0044) and the one the queue in
 * "Empresas que contratan" applies (`isUntaken`), so the tile and the tab it
 * opens count the same rows.
 */
const INBOUND_UNTAKEN: UrlFilter = { employer_key: ["is-null", null] };

/**
 * A legend for the drawer.
 *
 * This used to be a grid of bare links called "Ir a", and it existed because
 * two of the things it linked to had no drawer entry at all — which made the
 * home page the only way to reach a top-level noun, and made every other entry
 * on it look equally like a place you were supposed to have found on your own.
 *
 * The links are all in the drawer now, so what is left is the part that was
 * missing: what each section *is*. The groups and the order match the drawer
 * exactly, so this reads as an annotated copy of it rather than as a second,
 * competing map. `count` is resolved against the live snapshot, because half of
 * these sentences are only true while the numbers behind them hold — "casi
 * ninguno ha postulado" is a claim about 36 of 297, and it should stop being
 * made the day that changes.
 *
 * `company-members` and `profile-views` are deliberately absent, as they are in
 * the drawer: they are a join table and an audit trail, each reachable as a tab
 * on the record it belongs to.
 */
interface SectionEntry {
    label: string;
    href: string;
    /**
     * What this section is, for someone who has never seen it before.
     *
     * A function of the counts rather than a fixed string, so the sentence can
     * name the number that makes it surprising — "sólo 4 de las 107" is the
     * whole point of that line, and a hardcoded 4 becomes a lie the week
     * somebody publishes a fifth.
     */
    blurb: (c: Counts) => string;
    count?: (c: Counts) => number | null;
    Icon: React.ComponentType<any>;
}

interface DirectoryGroup {
    group: string;
    Icon: React.ComponentType<any>;
    entries: SectionEntry[];
}

const DIRECTORY: DirectoryGroup[] = [
    {
        group: "Vacantes",
        Icon: BriefcaseIcon,
        entries: [
            {
                label: "Por revisar",
                href: viewPath("ofertas-por-revisar"),
                blurb: () => "Vacantes que encontramos publicadas en otras webs de empleo. Todavía no están en nuestro tablón: aprobar una la copia.",
                count: c => c.pendingScraped,
                Icon: CloudDownloadIcon
            },
            {
                // The count is the list the link opens; the public board is a
                // smaller set whenever something is a draft or waiting for
                // review — 10 of the 49 on sustentalent_local — so it gets a
                // number of its own instead of lending its name to that one.
                label: "Vacantes",
                href: collectionPath("jobs"),
                blurb: c => `Abre en las que no están cerradas; ${num(c.publishedJobs)} ${plural(c.publishedJobs, "está", "están")} en el tablón público. En sólo ${num(c.internalJobs)} de ellas se postula dentro de SustenTalent; en las demás el botón lleva a LinkedIn o a la web de la empresa.`,
                count: c => c.jobsDefaultList,
                Icon: BriefcaseIcon
            }
        ]
    },
    {
        group: "Personas",
        Icon: UsersIcon,
        entries: [
            // One tile, where there were two. "Candidatos" pointed at the
            // `/candidatos` workspace and "Fichas de candidato" at the raw
            // table, and both counted `totalTalents` — the same number twice,
            // under two names, because they were two doors to one collection.
            // The queue is now a view mode of it, so there is one door and the
            // switcher is inside.
            {
                label: "Candidatos",
                href: collectionPath("talents"),
                blurb: c => `Abre en quien espera respuesta a su candidatura. ${num(c.totalTalents)} personas en la base; se mira un perfil cuando algo lo trae, no uno a uno.`,
                count: c => c.newApplications,
                Icon: UsersIcon
            }
        ]
    },
    {
        // Renamed to match the drawer, which has said "Marketing" for a while
        // now. This list claims below to be in the same order as the menu, and a
        // legend that names a section the menu does not have is worse than no
        // legend — it is a map of a building somebody remodelled.
        group: "Captación",
        Icon: CoinsIcon,
        entries: [
            {
                // Missing from this legend entirely until now, which is a strange
                // omission for the only entry here that is a queue: it is where
                // the outreach happens, and the home page did not mention it.
                label: "Empresas que contratan",
                href: viewPath("empresas-que-contratan"),
                blurb: c => c.untakenInbound
                    ? `Quién está contratando, a quién escribirle y qué toca hacer hoy. ${num(c.untakenInbound)} se registraron en la web y nadie las ha atendido.`
                    : "Quién está contratando, a quién escribirle y qué toca hacer hoy. Las que nos escriben entran aquí también.",
                count: c => c.prospects,
                Icon: HandshakeIcon
            },
            // "Leads comerciales" and "Mensajes de contacto" were listed here
            // as "the raw table" of the entry above. They left the drawer on
            // 2026-09-06 and leave this legend with it: a legend of the menu
            // that names screens the menu does not have is a map of a
            // building somebody remodelled.
        ]
    },
    {
        // Its own group, matching the drawer. It is reference data — the
        // employer directory the public board reads for logos and colours — and
        // not somewhere anybody prospects, which is why it stopped sharing a
        // heading with the commercial work.
        group: "Empresas",
        Icon: BuildingIcon,
        entries: [
            {
                label: "Fichas de empresa",
                href: collectionPath("companies"),
                blurb: c => `Sólo ${num(c.clientCompanies)} son empresas con las que hay relación. El resto son fichas que creó el sistema para poner un logo en una vacante — el filtro «Clientes» las separa.`,
                count: c => c.totalCompanies,
                Icon: BuildingIcon
            }
        ]
    },
    {
        // Its own group, matching the drawer. Not filed under "Empresas y
        // contacto" because the mailbox is not about companies: it holds
        // conversations with candidates, employers and people who are neither,
        // and putting it under one of the three nouns would claim it belongs to
        // that one.
        group: "Correo",
        Icon: MailIcon,
        entries: [
            {
                label: "Correo",
                href: viewPath("correo"),
                blurb: c => c.threadsAwaitingReply
                    ? `El buzón de hola@sustentalent.com, enviando y recibiendo. ${num(c.threadsAwaitingReply)} ${plural(c.threadsAwaitingReply, "conversación espera respuesta", "conversaciones esperan respuesta")}.`
                    : "El buzón de hola@sustentalent.com, enviando y recibiendo. Las respuestas llegan aquí solas.",
                count: c => c.unreadThreads,
                Icon: MailIcon
            }
        ]
    },
    {
        group: "Ajustes",
        Icon: UserCogIcon,
        entries: [
            {
                label: "Usuarios",
                href: collectionPath("users"),
                blurb: () => "Cuentas que pueden iniciar sesión: administradores, empresas y candidatos.",
                Icon: UserCogIcon
            }
        ]
    }
];

/**
 * A count, or `null` when the request that would have produced it failed.
 *
 * The distinction is load-bearing: 0 is a claim that nothing is waiting, and
 * making that claim because a query errored is how a queue gets ignored. `num`
 * renders null as "—".
 */
interface Counts {
    pendingScraped: number | null;
    newApplications: number | null;
    untriagedTalents: number | null;
    /**
     * Vacancies with no sustainability area, and how many of those are stored
     * as `{}` rather than NULL — see `JOBS_AREAS_EMPTY`. The split is what
     * decides whether "N sin áreas" can be a link.
     */
    jobsWithoutAreas: number | null;
    jobsWithoutAreasEmpty: number | null;
    jobsWithoutCompany: number | null;
    unreadMessages: number | null;
    /**
     * Conversations in the mailbox nobody has opened here, and the subset where
     * the last word was theirs.
     *
     * Two numbers because they answer different questions and only the second is
     * a queue: "sin leer" empties itself the moment somebody clicks, while
     * "esperan respuesta" only empties when somebody actually replies. A home
     * page that showed only the first would go quiet the day a person skimmed
     * the inbox without answering any of it.
     */
    unreadThreads: number | null;
    threadsAwaitingReply: number | null;
    /**
     * The outreach queues, which this page did not know existed.
     *
     * `prospectsDue` is the only one of the two that empties by itself: a
     * follow-up somebody scheduled and has now reached. `untakenInbound` is the
     * more valuable one — a company that wrote to us and has not been taken into
     * the list — and it is counted across both inbound tables because they are
     * the same event wearing two table names.
     */
    prospects: number | null;
    prospectsDue: number | null;
    untakenInbound: number | null;
    /**
     * Subsets of `newApplications` and `untakenInbound` that have aged past
     * their threshold — each its queue's own filter plus a date, so the amber
     * line under a tile counts that tile's rows and nobody else's.
     */
    overdueApplications: number | null;
    overdueInbound: number | null;
    /**
     * Candidates who registered in the last seven days. The one number about
     * supply worth a line on this page: "365 sin revisar" was the size of the
     * table and never went down, and a number that never goes down stops
     * being read.
     */
    newTalentsThisWeek: number | null;
    /**
     * The week's pulse (drizzle/0054 made two of these answerable). None is a
     * queue; together they say whether the machine is moving: what went out,
     * what came back, who arrived, who was approached.
     */
    sentThisWeek: number | null;
    repliedThisWeek: number | null;
    employersContactedThisWeek: number | null;
    inboundThisWeek: number | null;
    /** Reminders that came due — a queue, the one the week's pulse feeds. */
    followUpsDue: number | null;
    /** What the signed-in person owns, for the tiles' second line. */
    myThreadsWaiting: number | null;
    myFollowUpsDue: number | null;
    /**
     * Totals, for the legend at the bottom. These are not queues — nothing here
     * is waiting on anybody — so they are deliberately kept apart from the
     * counts above, which are all "how much work is outstanding".
     *
     * `jobsDefaultList` is what `/c/jobs` opens on (`JOBS_DEFAULT_LIST`);
     * `publishedJobs` is the public board and `internalJobs` the part of it
     * where the apply button stays inside SustenTalent.
     */
    jobsDefaultList: number | null;
    publishedJobs: number | null;
    internalJobs: number | null;
    totalTalents: number | null;
    totalApplications: number | null;
    totalCompanies: number | null;
    clientCompanies: number | null;
    totalLeads: number | null;
    totalMessages: number | null;
}

/**
 * What this page is, in one type: a set of counts.
 *
 * It used to carry `recentApplications` as well — rows, resolved client-side,
 * for the one card that listed them. Now that the card is gone every number
 * here is a number, and nothing on this page has to join two tables to render.
 */
interface Snapshot {
    counts: Counts;
}

/*
 * `OPEN_LEAD_STATUSES` used to live here, counting `company_leads.lead_status IN
 * (new, contacted)` for a tile that linked to the raw table. Both are gone: an
 * inbound lead is now worked from "Empresas que contratan", and once it has been
 * taken there `company_prospects.status` is the funnel — so a second, differently
 * spelled status counted on the home page could only ever disagree with it.
 */

/**
 * Fetch a page of a collection and return rows plus the server's own total, so
 * a headline number never silently becomes "however many we happened to load".
 */
async function page(
    client: RebaseClient,
    slug: string,
    params: Record<string, unknown>,
    failures?: Map<string, string>
) {
    // `data.collection(slug)` and not `data[slug]`: the property proxy resolves
    // an accessor by snake_casing it, so every hyphenated slug ("scraped-jobs")
    // becomes a table name that has no route ("scraped_jobs") and 404s at
    // request time. The explicit form passes the slug through untouched.
    try {
        const res = await (client.data as any).collection(slug).find(params);
        return { rows: res?.data ?? [], total: res?.meta?.total ?? (res?.data?.length ?? 0), ok: true };
    } catch (err: any) {
        // One collection failing must cost that collection and nothing else.
        // These fifteen requests shared a `Promise.all` inside a single
        // try/catch, so one drifted column — `companies.logo_on_dark`, once
        // deployed ahead of its migration — replaced the whole dashboard with
        // its error text. Everything else had loaded and was discarded, on the
        // screen whose entire job is "what needs me today".
        //
        // `ok: false` rather than a zero total, because those are different
        // claims: a tile reading 0 says "nothing is waiting", and saying that
        // when we merely failed to ask is worse than saying nothing at all.
        failures?.set(slug, err?.message || "no se pudo cargar");
        return { rows: [] as any[], total: 0, ok: false };
    }
}

/** ISO timestamp `days` in the past, for the "older than" filters. */
function cutoff(days: number): string {
    return new Date(Date.now() - days * 86_400_000).toISOString();
}

/**
 * Local midnight tonight, so `next_action_at < this` means "due today or before".
 *
 * Calendar days rather than elapsed hours, matching `dueState` in
 * `_prospects_shared.ts`. Counting hours would make this tile disagree with the
 * queue it links to every afternoon, which is how a number stops being believed.
 */
function startOfTomorrow(): string {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 1);
    return date.toISOString();
}

/**
 * A total, without the rows. `limit: 1` and only `meta.total` is read — the one
 * row that comes back is the cheapest page the API will serve, not a sample.
 */
async function total(
    client: RebaseClient,
    slug: string,
    where?: Record<string, unknown>,
    failures?: Map<string, string>
): Promise<number | null> {
    const res = await page(client, slug, where ? { where, limit: 1 } : { limit: 1 }, failures);
    // null, not 0 — see `page`. The legend renders it as "—".
    return res.ok ? res.total : null;
}

/**
 * Top-level "Hoy" workspace — the landing surface.
 *
 * Every other screen answers "what is in this table". This one answers "what
 * needs me", which is the question an operator actually opens the admin with.
 * Each block states a number, says plainly what it means, and links straight
 * into the work rather than into a filtered table the reader has to re-derive.
 */
export function HomeView() {
    const client = useRebaseClient<RebaseClient>();
    const { me } = useTeam();

    const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
    const [error, setError] = useState("");
    const [failures, setFailures] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!client?.data) return;
        setLoading(true);
        setError("");
        setFailures([]);
        // Collected rather than thrown: `page` records a failure here and
        // returns an empty result, so the requests below all settle and the
        // dashboard renders whatever did load.
        const failures = new Map<string, string>();
        try {
            // `limit: 1` where only the total matters — the count comes from
            // `meta.total`, so there is no reason to ship the rows.
            const [
                scrapedPending,
                applications,
                talents,
                leads,
                messages,
                overdueApplications,
                // Totals for the legend. Count-only, so ten of them cost less
                // than the single `talents` page above.
                totalApplications,
                totalCompanies,
                newTalentsThisWeek,
                sentThisWeek,
                repliedThisWeek,
                employersContactedThisWeek,
                inboundThisWeek,
                followUpsDue,
                myThreadsWaiting,
                myFollowUpsDue,
                clientCompanies,
                totalMessages,
                unreadThreads,
                threadsAwaitingReply,
                prospects,
                prospectsDue,
                untakenLeads,
                untakenMessages,
                overdueLeads,
                overdueMessages,
                jobsWithoutCompany,
                jobsAreasUnset,
                jobsAreasEmpty,
                jobsDefaultList,
                publishedJobs,
                internalJobs
            ] = await Promise.all([
                page(client, "scraped-jobs", {
                    where: { status: ["==", "pending"] },
                    orderBy: ["relevance_score", "desc"],
                    limit: 5
                }, failures),
                // Count-only, like the overdue query below it: the five rows
                // this used to bring back were the card that listed them by
                // name, and nothing reads them now.
                page(client, "talent-applications", {
                    where: { status: ["==", "applied"] },
                    limit: 1
                }, failures),
                // The whole roster in one request. It is ~240 rows, and it is
                // read for how many are untriaged — an emptiness test the
                // filter syntax can express, but not alongside the total this
                // same response carries. Filtering server-side to
                // `lead_status = new` would cost a second request to learn how
                // many candidates there are at all.
                page(client, "talents", { limit: 1000 }, failures),
                page(client, "company-leads", { limit: 500 }, failures),
                page(client, "contact-messages", {
                    where: { status: ["==", "new"] },
                    orderBy: ["created_at", "desc"],
                    limit: 5
                }, failures),
                // Count-only: the overdue half of the applications queue.
                // `limit: 1` because only `meta.total` is read. The inbound
                // queue's overdue half sits with its own counts further down.
                page(client, "talent-applications", {
                    where: {
                        status: ["==", "applied"],
                        created_at: ["<", cutoff(OVERDUE_DAYS.applications)]
                    },
                    limit: 1
                }, failures),
                total(client, "talent-applications", undefined, failures),
                total(client, "companies", undefined, failures),
                total(client, "talents", { created_at: [">", cutoff(7)] }, failures),
                total(client, "email-messages", { direction: ["==", "outbound"], status: ["==", "sent"], sent_at: [">", cutoff(7)] }, failures),
                total(client, "email-threads", { kind: ["==", "person"], last_inbound_at: [">", cutoff(7)] }, failures),
                total(client, "prospect-activity", { kind: ["==", "email"], created_at: [">", cutoff(7)] }, failures),
                total(client, "company-leads", { created_at: [">", cutoff(7)] }, failures),
                total(client, "email-threads", { kind: ["==", "person"], status: ["!=", "closed"], follow_up_at: ["<", startOfTomorrow()] }, failures),
                me ? total(client, "email-threads", { kind: ["==", "person"], status: ["!=", "closed"], last_direction: ["==", "inbound"], needs_reply: ["==", true], owner: ["==", me] }, failures) : Promise.resolve(null),
                me ? total(client, "email-threads", { kind: ["==", "person"], status: ["!=", "closed"], follow_up_at: ["<", startOfTomorrow()], owner: ["==", me] }, failures) : Promise.resolve(null),
                total(client, "companies", { source: ["in", ["signup", "manual"]] }, failures),
                total(client, "contact-messages", undefined, failures),
                // The mailbox. Count-only — the inbox screen is where the
                // conversations themselves belong, and this page only needs to
                // say how many are owed something.
                //
                // `status != closed` on both: a conversation somebody filed is
                // not work, and without that filter this number can only grow.
                // `kind = person` on both: a machine's mail owes nobody a reply,
                // and before 0051 half of this number was the tax office.
                total(client, "email-threads", {
                    unread: ["==", true],
                    status: ["!=", "closed"],
                    kind: ["==", "person"]
                }, failures),
                total(client, "email-threads", {
                    last_direction: ["==", "inbound"],
                    status: ["!=", "closed"],
                    kind: ["==", "person"],
                    // And their last message has to actually ask for something.
                    // Twenty-two of the twenty-nine this counted on 2026-09-06
                    // were the tax office or somebody saying "muchas gracias".
                    // See drizzle/0055 and services/email-reply.ts.
                    needs_reply: ["==", true]
                }, failures),
                // The outreach side. `next_action_at < tomorrow` is "due today
                // or overdue" — the same rule the workspace's own queue uses,
                // expressed as a filter the API can answer with a count.
                total(client, "company-prospects", undefined, failures),
                total(client, "company-prospects", {
                    next_action_at: ["<", startOfTomorrow()]
                }, failures),
                // Counts, not rows. These were two pages of 500 read to test
                // `employer_key` in the browser, because the filter syntax had
                // no null comparison; `is-null` exists now. The overdue half is
                // the same question plus a date. It used to be contact-form
                // messages still at `status = new`, which never counted a lead
                // at all and could count one somebody had already taken.
                total(client, "company-leads", INBOUND_UNTAKEN, failures),
                total(client, "contact-messages", INBOUND_UNTAKEN, failures),
                total(client, "company-leads", { ...INBOUND_UNTAKEN, created_at: ["<", cutoff(OVERDUE_DAYS.inbound)] }, failures),
                total(client, "contact-messages", { ...INBOUND_UNTAKEN, created_at: ["<", cutoff(OVERDUE_DAYS.inbound)] }, failures),
                // Asked of the server with the very filters their links carry,
                // where they used to be counted off a page of 500 jobs — which
                // also stopped them depending on that page holding every
                // vacancy there is.
                total(client, "jobs", JOBS_WITHOUT_COMPANY, failures),
                total(client, "jobs", JOBS_AREAS_UNSET, failures),
                total(client, "jobs", JOBS_AREAS_EMPTY, failures),
                // The legend's "Vacantes" line, off the same page of 500 until
                // now: its count had no filter at all, and the internal share
                // was taken over every vacancy, closed ones included.
                total(client, "jobs", JOBS_DEFAULT_LIST, failures),
                total(client, "jobs", JOBS_ON_BOARD, failures),
                total(client, "jobs", JOBS_ON_BOARD_INTERNAL, failures)
            ]);

            const talentRows = talents.rows as any[];
            const untriaged = talentRows.filter(t => (t.lead_status ?? "new") === "new");

            setSnapshot({
                counts: {
                    pendingScraped: scrapedPending.ok ? scrapedPending.total : null,
                    newApplications: applications.ok ? applications.total : null,
                    untriagedTalents: talents.ok ? untriaged.length : null,
                    jobsWithoutAreas: sum(jobsAreasUnset, jobsAreasEmpty),
                    jobsWithoutAreasEmpty: jobsAreasEmpty,
                    jobsWithoutCompany,
                    unreadMessages: messages.ok ? messages.total : null,
                    overdueApplications: overdueApplications.ok ? overdueApplications.total : null,
                    overdueInbound: sum(overdueLeads, overdueMessages),
                    jobsDefaultList,
                    publishedJobs,
                    // The number that makes the "Vacantes" line worth reading:
                    // how many of the board's postings actually produce a
                    // candidate on our side.
                    internalJobs,
                    totalTalents: talents.ok ? talents.total : null,
                    newTalentsThisWeek,
                    sentThisWeek,
                    repliedThisWeek,
                    employersContactedThisWeek,
                    inboundThisWeek,
                    followUpsDue,
                    myThreadsWaiting,
                    myFollowUpsDue,
                    totalApplications,
                    totalCompanies,
                    clientCompanies,
                    totalLeads: leads.total,
                    totalMessages,
                    unreadThreads,
                    threadsAwaitingReply,
                    prospects,
                    prospectsDue,
                    // null, not 0, when either table failed: "nobody wrote to
                    // us" and "we could not ask" are different claims and only
                    // one of them should make the tile go quiet.
                    untakenInbound: sum(untakenLeads, untakenMessages)
                }
            });
            setFailures([...failures].map(([slug, message]) => `${slug}: ${message}`));
        } catch (err: any) {
            setError(err?.message || "No se pudo cargar el resumen");
        } finally {
            setLoading(false);
        }
    }, [client, me]);

    useEffect(() => {
        void load();
    }, [load]);

    const c = snapshot?.counts;

    /**
     * Three tiles, in order of money, each with the verb that empties it.
     *
     * The row used to be six equal cells, and two of them were the size of a
     * table — 365 candidates "sin revisar", 186 offers "por revisar" — numbers
     * that never went down beside three that a person could actually act on
     * by lunchtime. Six equal weights is no order, and a number that never
     * changes trains the eye to skip the row.
     *
     * So: what an employer paid for, what a person is waiting on, what came
     * back in the mailbox. Each links into the tab where the work is done, and
     * the label is what you do there, not what the rows are. The rest of the
     * old row survives as one quiet line below.
     */
    const primary = useMemo(() => {
        if (!c) return [];
        return [
            {
                // First: an employer who wrote to us is worth many times one we
                // found, and on 2026-09-05 one of them had asked for a
                // recruitment process two days earlier and nobody had opened it.
                key: "inbound",
                value: c.untakenInbound,
                noun: plural(c.untakenInbound, "empresa se registró en la web", "empresas se registraron en la web"),
                verb: "Atender",
                blurb: "Rellenaron el formulario de empresas o el de contacto en sustentalent.com y nadie las ha atendido.",
                href: viewPath("empresas-que-contratan"),
                Icon: BuildingIcon,
                overdue: c.overdueInbound,
                overdueLabel: `${num(c.overdueInbound)} ${plural(c.overdueInbound, "lleva", "llevan")} más de ${OVERDUE_DAYS.inbound} días`
            },
            {
                key: "applications",
                value: c.newApplications,
                noun: plural(c.newApplications, "candidatura sin responder", "candidaturas sin responder"),
                verb: "Contestar",
                blurb: "Personas que se postularon a una vacante y no han recibido respuesta.",
                href: collectionPath("talents"),
                Icon: InboxIcon,
                overdue: c.overdueApplications,
                overdueLabel: `${num(c.overdueApplications)} ${plural(c.overdueApplications, "lleva", "llevan")} más de ${OVERDUE_DAYS.applications} días`
            },
            {
                // "waiting on a reply", not "unread": unread empties itself the
                // moment somebody clicks into the inbox, so a home page built on
                // it goes quiet after a skim while the replies are unwritten.
                key: "correo",
                value: c.threadsAwaitingReply,
                noun: plural(c.threadsAwaitingReply, "conversación espera respuesta", "conversaciones esperan respuesta"),
                verb: "Responder",
                blurb: "Escribieron al buzón y todavía no les hemos contestado.",
                href: viewPath("correo"),
                Icon: MailIcon,
                overdue: c.unreadThreads,
                overdueLabel: `${num(c.unreadThreads)} sin abrir`,
                mine: c.myThreadsWaiting
            }
        ];
    }, [c]);

    /**
     * The second line: things that move on their own schedule, and one
     * signal about supply. None of these is a person waiting on us.
     */
    const secondary = useMemo(() => {
        if (!c) return [];
        return [
            {
                key: "followups",
                value: c.followUpsDue,
                label: plural(c.followUpsDue, "conversación para retomar hoy", "conversaciones para retomar hoy")
                    + (c.myFollowUpsDue ? ` (${num(c.myFollowUpsDue)} ${plural(c.myFollowUpsDue, "tuya", "tuyas")})` : ""),
                href: viewPath("correo")
            },
            { key: "prospects", value: c.prospectsDue, label: plural(c.prospectsDue, "empresa para seguir hoy", "empresas para seguir hoy"), href: viewPath("empresas-que-contratan") },
            { key: "scraped", value: c.pendingScraped, label: plural(c.pendingScraped, "vacante por revisar", "vacantes por revisar"), href: viewPath("ofertas-por-revisar") }
        ];
    }, [c]);

    /**
     * Where "N sin áreas de sostenibilidad" leads: the filter for whichever way
     * all N are stored, or nowhere. When they are a mix of NULL and `{}` no list
     * the table can open shows exactly them, and a number that links to the
     * wrong rows is the thing this line used to do — so it stays plain text.
     */
    const areasHref = !c || c.jobsWithoutAreasEmpty === null
        ? null
        : c.jobsWithoutAreasEmpty === 0
            ? filteredCollectionPath("jobs", JOBS_AREAS_UNSET)
            : c.jobsWithoutAreasEmpty === c.jobsWithoutAreas
                ? filteredCollectionPath("jobs", JOBS_AREAS_EMPTY)
                : null;

    return (
        <div className="flex flex-col h-full w-full overflow-y-auto bg-surface-sheet">
            {/*
                7xl rather than 6xl: the queue row is six cells wide now, and at
                1152px each one was ~180px — enough for the number but not for a
                two-word label to stay on two lines. A dashboard is scanned
                across, so the extra 128px goes to the row that is scanned.
            */}
            <div className="max-w-7xl mx-auto w-full p-6 flex flex-col gap-6">

                <div className="flex items-center gap-3">
                    <div className="grow">
                        <Typography variant="h4" component="h1">Hoy</Typography>
                        <Typography variant="body1" color="secondary">
                            Lo que está esperando una decisión.
                        </Typography>
                    </div>
                    <Tooltip title="Recargar" asChild>
                        <IconButton size="small" aria-label="Recargar" onClick={load} disabled={loading} aria-busy={loading}>
                            <RefreshCwIcon size={iconSize.small} className={cls(loading && "animate-spin")} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Only a total failure takes the page. Anything less is the
                    warning below, over a dashboard that still renders. */}
                {error && <Alert color="error">{error}</Alert>}

                {failures.length > 0 && (
                    <Alert color="warning">
                        <div className="flex flex-col gap-1">
                            <span>
                                Algunos datos no se pudieron cargar; lo demás está al día.
                                Las cifras afectadas aparecen como “—”.
                            </span>
                            {failures.map(f => (
                                <span key={f} className="text-xs opacity-80">{f}</span>
                            ))}
                        </div>
                    </Alert>
                )}

                {!snapshot && !error && <CircularProgressCenter />}

                {snapshot && (
                    <>
                        {/* ── The three things that are money ── */}
                        <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
                            {primary.map(tile => {
                                // Three states, in order of precedence: something
                                // is late, there is work, there is nothing.
                                // An unknown value is not "calm": calm is the
                                // faded style that means "this queue is empty",
                                // and that is precisely the thing we cannot say.
                                const tone = tile.value === null
                                    ? "pending"
                                    : (tile.overdue ?? 0) > 0
                                        ? "overdue"
                                        : tile.value > 0 ? "pending" : "calm";
                                const style = TONE_STYLES[tone];
                                const Icon = tile.Icon;
                                return (
                                    <a key={tile.key} href={tile.href} className="no-underline" title={tile.blurb}>
                                        <Card className={cls(
                                            "px-4 py-4 flex flex-col gap-2 h-full",
                                            cardClickableMixin,
                                            style.ring
                                        )}>
                                            <div className="flex items-center gap-2">
                                                <span className={cls(
                                                    "rounded-md p-1 flex items-center justify-center shrink-0",
                                                    style.icon
                                                )}>
                                                    <Icon size={16} />
                                                </span>
                                                <Typography
                                                    variant="h3"
                                                    component="p"
                                                    className={cls("grow tabular-nums", style.value)}
                                                >
                                                    {num(tile.value)}
                                                </Typography>
                                            </div>
                                            <Typography variant="body1" component="p" className="leading-snug">
                                                {tile.noun}
                                            </Typography>
                                            {tone === "overdue" && tile.overdueLabel && (
                                                <Typography
                                                    variant="body2"
                                                    className="text-amber-600 dark:text-amber-400 leading-snug"
                                                >
                                                    {tile.overdueLabel}
                                                </Typography>
                                            )}
                                            {(tile as any).mine ? (
                                                <Typography variant="body2" color="secondary" className="leading-snug">
                                                    {num((tile as any).mine)} tuya(s)
                                                </Typography>
                                            ) : null}
                                            {/* The verb, only when there is something to do it to. */}
                                            <Typography
                                                variant="body2"
                                                component="p"
                                                className={cls("mt-auto pt-1 font-medium", tone === "calm" ? "opacity-40" : "text-primary")}
                                            >
                                                {tone === "calm" ? "Nada pendiente" : `${tile.verb} →`}
                                            </Typography>
                                        </Card>
                                    </a>
                                );
                            })}
                        </div>

                        {/* ── The week's pulse ──
                            Not queues: nothing here is waiting on anybody. It
                            is the four numbers that say whether the machine
                            moved this week — out, back, arrived, approached —
                            which a home page made only of backlogs never said. */}
                        <Typography variant="body2" color="secondary">
                            Esta semana:{" "}
                            <span className="tabular-nums font-medium text-text-primary dark:text-text-primary-dark">{num(c!.sentThisWeek)}</span> {plural(c!.sentThisWeek, "correo enviado", "correos enviados")} ·{" "}
                            <span className="tabular-nums font-medium text-text-primary dark:text-text-primary-dark">{num(c!.repliedThisWeek)}</span> {plural(c!.repliedThisWeek, "conversación con respuesta", "conversaciones con respuesta")} ·{" "}
                            <span className="tabular-nums font-medium text-text-primary dark:text-text-primary-dark">{num(c!.newTalentsThisWeek)}</span> {plural(c!.newTalentsThisWeek, "candidato nuevo", "candidatos nuevos")} ·{" "}
                            <span className="tabular-nums font-medium text-text-primary dark:text-text-primary-dark">{num(c!.employersContactedThisWeek)}</span> {plural(c!.employersContactedThisWeek, "empresa contactada", "empresas contactadas")} ·{" "}
                            <span className="tabular-nums font-medium text-text-primary dark:text-text-primary-dark">{num(c!.inboundThisWeek)}</span> {plural(c!.inboundThisWeek, "empresa se registró en la web", "empresas se registraron en la web")}
                        </Typography>

                        {/* ── The rest, one line ── */}
                        <div className="flex flex-wrap gap-x-5 gap-y-1">
                            {secondary.map(item => (
                                <a key={item.key} href={item.href} className="no-underline">
                                    <Typography variant="body2" color="secondary" className="hover:underline">
                                        <span className={cls("tabular-nums font-medium", (item.value ?? 0) > 0 ? "text-text-primary dark:text-text-primary-dark" : "")}>
                                            {num(item.value)}
                                        </span>
                                        {" "}{item.label}
                                    </Typography>
                                </a>
                            ))}
                        </div>

                        {/* ── Data-quality warnings, only when there is one ── */}
                        {/*
                            Housekeeping, in the tone of housekeeping. This was
                            an amber alert bar under the queues, and amber on
                            this page means "a person has waited too long"; a
                            vacancy missing its company link is nobody waiting.
                            "Matching" is not mentioned any more: it was deleted.
                        */}
                        {((c!.jobsWithoutAreas ?? 0) > 0 || (c!.jobsWithoutCompany ?? 0) > 0) && (
                            <Typography variant="body2" color="secondary">
                                Datos por completar:
                                {(c!.jobsWithoutCompany ?? 0) > 0 && (
                                    <> <a href={filteredCollectionPath("jobs", JOBS_WITHOUT_COMPANY)} className="hover:underline">{c!.jobsWithoutCompany} {plural(c!.jobsWithoutCompany, "vacante sin ficha de empresa", "vacantes sin ficha de empresa")}</a></>
                                )}
                                {(c!.jobsWithoutCompany ?? 0) > 0 && (c!.jobsWithoutAreas ?? 0) > 0 && " · "}
                                {(c!.jobsWithoutAreas ?? 0) > 0 && (
                                    areasHref
                                        ? <> <a href={areasHref} className="hover:underline">{c!.jobsWithoutAreas} sin áreas de sostenibilidad</a></>
                                        : <> {c!.jobsWithoutAreas} sin áreas de sostenibilidad</>
                                )}
                            </Typography>
                        )}

                        {/*
                            "Postulaciones sin responder" used to be here: a
                            card listing the open applications by name, each one
                            opening the raw `talent_applications` row in the side
                            panel. It went because the row it opened is the worst
                            surface in the product for the thing it names —
                            `talent_applications` is a join table, so the panel
                            showed two uuids and a status where the reader
                            expected a person — and because the same people are
                            now one click away in a place where they can actually
                            be answered: the count above links to Candidatos,
                            whose queue leads with exactly this population and
                            whose cover carries the composer.

                            The number stays. What went is a second, worse list
                            of the same population, and with it the last reason
                            this page had to render rows at all.
                        */}

                        {/* ── The legend for the drawer ──
                            Two columns rather than four: these entries are a
                            sentence each now, and four columns gave each one a
                            ~200px measure that broke every line into three. */}
                        <div className="flex flex-col gap-3">
                            <div>
                                <Typography variant="subtitle2" component="h2">Qué hay en cada sección</Typography>
                                <Typography variant="body2" color="secondary">
                                    Mismo orden que el menú de la izquierda.
                                </Typography>
                            </div>
                            <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
                                {DIRECTORY.map(section => {
                                    const GroupIcon = section.Icon;
                                    return (
                                        <div
                                            key={section.group}
                                            className={cls("border rounded-lg overflow-hidden flex flex-col", defaultBorderMixin)}
                                        >
                                            <div className={cls(
                                                "px-3 py-2.5 flex items-center gap-2 border-b",
                                                defaultBorderMixin
                                            )}>
                                                <span className="rounded-md p-1 flex items-center justify-center text-text-secondary dark:text-text-secondary-dark bg-surface-raised">
                                                    <GroupIcon size={14} />
                                                </span>
                                                <Typography variant="subtitle2" component="h3">
                                                    {section.group}
                                                </Typography>
                                            </div>
                                            <div className="p-1 flex flex-col">
                                                {section.entries.map(entry => {
                                                    const EntryIcon = entry.Icon;
                                                    const value = entry.count?.(c!);
                                                    return (
                                                        <a
                                                            key={entry.href}
                                                            href={entry.href}
                                                            className={cls("no-underline rounded-md px-2 py-2 flex items-start gap-2.5", surfaceHoverMixin)}
                                                        >
                                                            <span className="text-text-secondary dark:text-text-secondary-dark mt-0.5 shrink-0">
                                                                <EntryIcon size={15} />
                                                            </span>
                                                            <span className="min-w-0 grow">
                                                                <span className="flex items-baseline gap-2">
                                                                    <Typography variant="body2">
                                                                        {entry.label}
                                                                    </Typography>
                                                                    {value !== undefined && (
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="secondary"
                                                                            className="tabular-nums"
                                                                        >
                                                                            {value}
                                                                        </Typography>
                                                                    )}
                                                                </span>
                                                                <Typography variant="body2" color="secondary">
                                                                    {entry.blurb(c!)}
                                                                </Typography>
                                                            </span>
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

